import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useMemo,
} from 'react';
import {
    fetchAgents,
    fetchLeads,
    fetchSupervisors,
    fetchOms,
    UserDetails,
} from '../common/APIUtils';
import { UserPermissions } from '../common/UserPermissions';


interface EmployeeDataContextValue {
    agents: UserDetails[];
    leads: UserDetails[];
    supervisors: UserDetails[];
    oms: UserDetails[];
    loading: boolean;
    error: string | null;
}

const EmployeeDataContext = createContext<EmployeeDataContextValue>({
    agents: [],
    leads: [],
    supervisors: [],
    oms: [],
    loading: false,
    error: null,
});

export enum UserRole {
    Admin = 'Admin',
    Agent = 'Agent',
    Csqa = 'Csqa',
    CsqaManagement = 'CsqaManagement',
    Hr = 'Hr',
    Lead = 'Lead',
    Om = 'Om',
    SeniorOm = 'SeniorOm',
    Supervisor = 'Supervisor',
}

interface EmployeeDataProviderProps {
    user: string;
    role?: UserRole;
    userPermissions: Set<string>;
    children: React.ReactNode;
}

export const EmployeeDataProvider: React.FC<EmployeeDataProviderProps> = ({
    user,
    role,
    userPermissions,
    children,
}) => {
    const [agents, setAgents] = useState<UserDetails[]>([]);
    const [leads, setLeads] = useState<UserDetails[]>([]);
    const [supervisors, setSupervisors] = useState<UserDetails[]>([]);
    const [oms, setOms] = useState<UserDetails[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * We only want to fetch employees if:
     *  - The user has the relevant permissions to search or create Csqa/Coaching.
     */
    const hasPermissions = useMemo(() =>
        [
            UserPermissions.CreateCoaching,
            UserPermissions.CreateCsqa,
            UserPermissions.SearchCoaching,
            UserPermissions.SearchCsqa,
        ].some(permission => userPermissions.has(permission)),
        [userPermissions]
    );

    useEffect(() => {
        if (!user || userPermissions.size === 0) {
            return;
        }

        let isMounted = true;

        const fetchAll = async () => {
            setLoading(true);
            setError(null);

            try {
                // We pass in 'user' + 'role' so the scoping can occur
                const [agentsData, leadsData, supervisorsData, omsData] = await Promise.all([
                    fetchAgents(user, role),
                    fetchLeads(user, role),
                    fetchSupervisors(user, role),
                    fetchOms(user, role),
                ]);

                if (isMounted) {
                    setAgents(agentsData);
                    setLeads(leadsData);
                    setSupervisors(supervisorsData);
                    setOms(omsData);
                }
            } catch (err: any) {
                console.error('EmployeeDataProvider error:', err);
                if (isMounted) {
                    setError(err.message || 'Error fetching employees');
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        if (hasPermissions) {
            fetchAll();
        } else {
            // If user does not have permissions, set them empty
            setAgents([]);
            setLeads([]);
            setSupervisors([]);
            setOms([]);
        }

        return () => {
            isMounted = false;
        };
    }, [hasPermissions, user, role]);

    const value: EmployeeDataContextValue = {
        agents,
        leads,
        supervisors,
        oms,
        loading,
        error,
    };

    return (
        <EmployeeDataContext.Provider value={value}>
            {children}
        </EmployeeDataContext.Provider>
    );
};

export const useEmployeeData = () => {
    return useContext(EmployeeDataContext);
};
